// src/store/slices/driverSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import driverService from '../../services/driver.service';

const initialState = {
  profile: null,
  stats: null,
  detailedStats: null,
  earnings: null,
  detailedEarnings: null,
  currentDeliveries: [],
  availableDeliveries: [],
  deliveryHistory: { deliveries: [], pagination: null },
  currentDelivery: null, // Single delivery for detail view
  isAvailable: false,
  status: 'offline',
  currentLocation: null,
  loading: {
    profile: false,
    stats: false,
    detailedStats: false,
    earnings: false,
    detailedEarnings: false,
    currentDeliveries: false,
    availableDeliveries: false,
    history: false,
    status: false,
    availability: false,
    location: false,
    claimDelivery: false,
    updateDeliveryStatus: false
  },
  error: null
};

// ============================================================================
// 📋 THUNKS - PROFIL LIVREUR
// ============================================================================

export const fetchMyProfile = createAsyncThunk(
  'driver/fetchMyProfile',
  async (_, { rejectWithValue }) => {
    try {
      const data = await driverService.getMyProfile();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMyProfile = createAsyncThunk(
  'driver/updateMyProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const data = await driverService.updateMyProfile(profileData);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// 📍 THUNKS - DISPONIBILITÉ ET STATUT
// ============================================================================

export const updateMyAvailability = createAsyncThunk(
  'driver/updateMyAvailability',
  async (isAvailable, { rejectWithValue }) => {
    try {
      const data = await driverService.updateMyAvailability(isAvailable);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateMyStatus = createAsyncThunk(
  'driver/updateMyStatus',
  async (status, { rejectWithValue }) => {
    try {
      const data = await driverService.updateMyStatus(status);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// 📍 THUNKS - LOCALISATION GPS
// ============================================================================

export const updateMyLocation = createAsyncThunk(
  'driver/updateMyLocation',
  async (location, { rejectWithValue }) => {
    try {
      const data = await driverService.updateMyLocation(location);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getMyLocation = createAsyncThunk(
  'driver/getMyLocation',
  async (_, { rejectWithValue }) => {
    try {
      const data = await driverService.getMyLocation();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// 📊 THUNKS - STATISTIQUES
// ============================================================================

export const fetchMyStats = createAsyncThunk(
  'driver/fetchMyStats',
  async (period = '30d', { rejectWithValue }) => {
    try {
      const data = await driverService.getMyStats(period);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMyDetailedStats = createAsyncThunk(
  'driver/fetchMyDetailedStats',
  async (period = '30d', { rejectWithValue }) => {
    try {
      const data = await driverService.getMyDetailedStats(period);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// 💰 THUNKS - GAINS ET REVENUS
// ============================================================================

export const fetchMyEarnings = createAsyncThunk(
  'driver/fetchMyEarnings',
  async (period = '30d', { rejectWithValue }) => {
    try {
      const data = await driverService.getMyEarnings(period);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMyDetailedEarnings = createAsyncThunk(
  'driver/fetchMyDetailedEarnings',
  async (params, { rejectWithValue }) => {
    try {
      const data = await driverService.getMyDetailedEarnings(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// 📦 THUNKS - LIVRAISONS
// ============================================================================

export const fetchCurrentDeliveries = createAsyncThunk(
  'driver/fetchCurrentDeliveries',
  async (_, { rejectWithValue }) => {
    try {
      const data = await driverService.getMyCurrentDeliveries();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAvailableDeliveries = createAsyncThunk(
  'driver/fetchAvailableDeliveries',
  async (_, { rejectWithValue }) => {
    try {
      const data = await driverService.getAvailableDeliveries();
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMyDeliveryHistory = createAsyncThunk(
  'driver/fetchMyDeliveryHistory',
  async (params, { rejectWithValue }) => {
    try {
      const data = await driverService.getMyDeliveryHistory(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getMyDeliveryDetails = createAsyncThunk(
  'driver/getMyDeliveryDetails',
  async (shippingId, { rejectWithValue }) => {
    try {
      const data = await driverService.getMyDeliveryDetails(shippingId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Alias for fetching delivery history (used by MissionsPage)
export const fetchDeliveryHistory = fetchMyDeliveryHistory;

// Fetch single delivery by ID (for detail view)
export const fetchDeliveryById = createAsyncThunk(
  'driver/fetchDeliveryById',
  async (deliveryId, { rejectWithValue, getState }) => {
    try {
      // First check if it's in current deliveries
      const state = getState();
      const inCurrent = state.driver.currentDeliveries?.find(d => d.id === deliveryId);
      if (inCurrent) return inCurrent;

      // Check in available deliveries
      const available = state.driver.availableDeliveries;
      const inAvailable = Array.isArray(available)
        ? available.find(d => d.id === deliveryId)
        : available?.deliveries?.find(d => d.id === deliveryId);
      if (inAvailable) return inAvailable;

      // Otherwise fetch from API
      const data = await driverService.getMyDeliveryDetails(deliveryId);
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// 🎯 THUNKS - GESTION DES LIVRAISONS
// ============================================================================

export const claimDelivery = createAsyncThunk(
  'driver/claimDelivery',
  async (deliveryId, { rejectWithValue, dispatch }) => {
    try {
      const data = await driverService.acceptDelivery(deliveryId);
      // Rafraîchir les livraisons disponibles après acceptation
      dispatch(fetchAvailableDeliveries());
      dispatch(fetchCurrentDeliveries());
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateDeliveryStatus = createAsyncThunk(
  'driver/updateDeliveryStatus',
  async ({ deliveryId, status, reason, delivery_notes, proof_photo, confirmationToken }, { rejectWithValue, dispatch }) => {
    try {
      const data = await driverService.updateDeliveryStatus(deliveryId, status, reason, confirmationToken);
      // Rafraîchir les livraisons en cours après mise à jour
      dispatch(fetchCurrentDeliveries());
      // Si livraison terminée, rafraîchir aussi l'historique
      if (['delivered', 'failed_delivery', 'returned'].includes(status)) {
        dispatch(fetchMyDeliveryHistory());
      }
      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// 🔧 SLICE PRINCIPAL
// ============================================================================

const driverSlice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentLocation: (state, action) => {
      state.currentLocation = action.payload;
    },
    setIsAvailable: (state, action) => {
      state.isAvailable = action.payload;
    },
    toggleAvailabilityOptimistic: (state) => {
      state.isAvailable = !state.isAvailable;
    },
    updateStatusOptimistic: (state, action) => {
      state.status = action.payload;
    },
    rollbackAvailability: (state, action) => {
      state.isAvailable = action.payload;
    },
    rollbackStatus: (state, action) => {
      state.status = action.payload;
    },
    resetDeliveryError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // ============================================================================
      // 📋 PROFIL LIVREUR
      // ============================================================================
      .addCase(fetchMyProfile.pending, (state) => {
        state.loading.profile = true;
        state.error = null;
      })
      .addCase(fetchMyProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.profile = action.payload;
        // getDriverProfile() (backend) renvoie le statut sous "status", pas
        // "driver_status" -- sans cette normalisation, profile.driver_status
        // est undefined a chaque chargement/actualisation, et l'UI affiche
        // "hors ligne" meme quand le livreur est reellement en ligne (bug
        // invisible cote backend, le vrai statut n'est jamais touche).
        state.profile.driver_status = action.payload.driver_status || action.payload.status;
        state.isAvailable = action.payload.is_available || false;
        state.status = action.payload.driver_status || action.payload.status || 'offline';
      })
      .addCase(fetchMyProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload;
      })
      
      .addCase(updateMyProfile.pending, (state) => {
        state.loading.profile = true;
      })
      .addCase(updateMyProfile.fulfilled, (state, action) => {
        state.loading.profile = false;
        state.profile = { ...state.profile, ...action.payload };
      })
      .addCase(updateMyProfile.rejected, (state, action) => {
        state.loading.profile = false;
        state.error = action.payload;
      })

      // ============================================================================
      // 📍 DISPONIBILITÉ ET STATUT
      // ============================================================================
      .addCase(updateMyAvailability.pending, (state) => {
        state.loading.availability = true;
      })
      .addCase(updateMyAvailability.fulfilled, (state, action) => {
        state.loading.availability = false;
        state.isAvailable = action.payload.is_available;
        state.status = action.payload.driver_status || action.payload.status;
        if (state.profile) {
          state.profile.is_available = action.payload.is_available;
          state.profile.driver_status = action.payload.driver_status || action.payload.status;
        }
      })
      .addCase(updateMyAvailability.rejected, (state, action) => {
        state.loading.availability = false;
        state.error = action.payload;
        // Rollback optimistic update
        state.isAvailable = !state.isAvailable;
      })

      .addCase(updateMyStatus.pending, (state) => {
        state.loading.status = true;
      })
      .addCase(updateMyStatus.fulfilled, (state, action) => {
        state.loading.status = false;
        state.status = action.payload.driver_status || action.payload.status;
        state.isAvailable = action.payload.is_available;
        if (state.profile) {
          state.profile.driver_status = action.payload.driver_status || action.payload.status;
          state.profile.is_available = action.payload.is_available;
        }
      })
      .addCase(updateMyStatus.rejected, (state, action) => {
        state.loading.status = false;
        state.error = action.payload;
      })

      // ============================================================================
      // 📍 LOCALISATION GPS
      // ============================================================================
      .addCase(updateMyLocation.pending, (state) => {
        state.loading.location = true;
      })
      .addCase(updateMyLocation.fulfilled, (state, action) => {
        state.loading.location = false;
        state.currentLocation = action.payload.location || action.payload;
      })
      .addCase(updateMyLocation.rejected, (state, action) => {
        state.loading.location = false;
        state.error = action.payload;
      })

      .addCase(getMyLocation.pending, (state) => {
        state.loading.location = true;
      })
      .addCase(getMyLocation.fulfilled, (state, action) => {
        state.loading.location = false;
        state.currentLocation = action.payload;
      })
      .addCase(getMyLocation.rejected, (state, action) => {
        state.loading.location = false;
        state.error = action.payload;
      })

      // ============================================================================
      // 📊 STATISTIQUES
      // ============================================================================
      .addCase(fetchMyStats.pending, (state) => {
        state.loading.stats = true;
      })
      .addCase(fetchMyStats.fulfilled, (state, action) => {
        state.loading.stats = false;
        state.stats = action.payload;
      })
      .addCase(fetchMyStats.rejected, (state, action) => {
        state.loading.stats = false;
        state.error = action.payload;
      })

      .addCase(fetchMyDetailedStats.pending, (state) => {
        state.loading.detailedStats = true;
      })
      .addCase(fetchMyDetailedStats.fulfilled, (state, action) => {
        state.loading.detailedStats = false;
        state.detailedStats = action.payload;
      })
      .addCase(fetchMyDetailedStats.rejected, (state, action) => {
        state.loading.detailedStats = false;
        state.error = action.payload;
      })

      // ============================================================================
      // 💰 GAINS ET REVENUS
      // ============================================================================
      .addCase(fetchMyEarnings.pending, (state) => {
        state.loading.earnings = true;
      })
      .addCase(fetchMyEarnings.fulfilled, (state, action) => {
        state.loading.earnings = false;
        state.earnings = action.payload;
      })
      .addCase(fetchMyEarnings.rejected, (state, action) => {
        state.loading.earnings = false;
        state.error = action.payload;
      })

      .addCase(fetchMyDetailedEarnings.pending, (state) => {
        state.loading.detailedEarnings = true;
      })
      .addCase(fetchMyDetailedEarnings.fulfilled, (state, action) => {
        state.loading.detailedEarnings = false;
        state.detailedEarnings = action.payload;
      })
      .addCase(fetchMyDetailedEarnings.rejected, (state, action) => {
        state.loading.detailedEarnings = false;
        state.error = action.payload;
      })

      // ============================================================================
      // 📦 LIVRAISONS
      // ============================================================================
      .addCase(fetchCurrentDeliveries.pending, (state) => {
        state.loading.currentDeliveries = true;
      })
      .addCase(fetchCurrentDeliveries.fulfilled, (state, action) => {
        state.loading.currentDeliveries = false;
        state.currentDeliveries = action.payload.deliveries || action.payload;
      })
      .addCase(fetchCurrentDeliveries.rejected, (state, action) => {
        state.loading.currentDeliveries = false;
        state.error = action.payload;
      })

      .addCase(fetchAvailableDeliveries.pending, (state) => {
        state.loading.availableDeliveries = true;
      })
      .addCase(fetchAvailableDeliveries.fulfilled, (state, action) => {
        state.loading.availableDeliveries = false;
        state.availableDeliveries = action.payload;
      })
      .addCase(fetchAvailableDeliveries.rejected, (state, action) => {
        state.loading.availableDeliveries = false;
        state.error = action.payload;
      })

      .addCase(fetchMyDeliveryHistory.pending, (state) => {
        state.loading.history = true;
      })
      .addCase(fetchMyDeliveryHistory.fulfilled, (state, action) => {
        state.loading.history = false;
        state.deliveryHistory = action.payload;
      })
      .addCase(fetchMyDeliveryHistory.rejected, (state, action) => {
        state.loading.history = false;
        state.error = action.payload;
      })

      // ============================================================================
      // 🎯 GESTION DES LIVRAISONS
      // ============================================================================
      .addCase(claimDelivery.pending, (state, action) => {
        state.loading.claimDelivery = true;
        state.error = null;
      })
      .addCase(claimDelivery.fulfilled, (state, action) => {
        state.loading.claimDelivery = false;
        // Gérer les deux structures possibles de données
        if (state.availableDeliveries.deliveries) {
          state.availableDeliveries.deliveries = state.availableDeliveries.deliveries.filter(
            d => d.id !== action.payload.id
          );
        } else if (Array.isArray(state.availableDeliveries)) {
          state.availableDeliveries = state.availableDeliveries.filter(
            d => d.id !== action.payload.id
          );
        }
        // Ajouter aux livraisons en cours
        state.currentDeliveries.push(action.payload);
        // Mettre à jour le profil si présent
        if (state.profile) {
          state.profile.current_deliveries_count = (state.profile.current_deliveries_count || 0) + 1;
        }
      })
      .addCase(claimDelivery.rejected, (state, action) => {
        state.loading.claimDelivery = false;
        state.error = action.payload;
      })

      .addCase(updateDeliveryStatus.pending, (state) => {
        state.loading.updateDeliveryStatus = true;
        state.error = null;
      })
      .addCase(updateDeliveryStatus.fulfilled, (state, action) => {
        state.loading.updateDeliveryStatus = false;
        // Mettre à jour dans les livraisons en cours
        const index = state.currentDeliveries.findIndex(
          d => d.id === action.payload.id
        );
        if (index !== -1) {
          state.currentDeliveries[index] = action.payload;
          
          // Si livraison terminée, retirer des livraisons en cours
          if (['delivered', 'failed_delivery', 'returned'].includes(action.payload.status)) {
            state.currentDeliveries.splice(index, 1);
            
            // Mettre à jour le profil
            if (state.profile) {
              state.profile.current_deliveries_count = Math.max(0, (state.profile.current_deliveries_count || 1) - 1);
              
              // Mettre à jour les statistiques
              if (action.payload.status === 'delivered') {
                state.profile.successful_deliveries = (state.profile.successful_deliveries || 0) + 1;
              } else {
                state.profile.failed_deliveries = (state.profile.failed_deliveries || 0) + 1;
              }
              state.profile.total_deliveries = (state.profile.total_deliveries || 0) + 1;
            }
          }
        }
      })
      .addCase(updateDeliveryStatus.rejected, (state, action) => {
        state.loading.updateDeliveryStatus = false;
        state.error = action.payload;
      })

      // ============================================================================
      // 📋 FETCH SINGLE DELIVERY BY ID
      // ============================================================================
      .addCase(fetchDeliveryById.pending, (state) => {
        state.loading.currentDeliveries = true;
        state.error = null;
      })
      .addCase(fetchDeliveryById.fulfilled, (state, action) => {
        state.loading.currentDeliveries = false;
        state.currentDelivery = action.payload;
      })
      .addCase(fetchDeliveryById.rejected, (state, action) => {
        state.loading.currentDeliveries = false;
        state.error = action.payload;
      });
  }
});

// ============================================================================
// 🎯 ACTIONS ET SÉLECTEURS
// ============================================================================

export const { 
  clearError, 
  setCurrentLocation, 
  setIsAvailable,
  toggleAvailabilityOptimistic,
  updateStatusOptimistic,
  rollbackAvailability,
  rollbackStatus,
  resetDeliveryError
} = driverSlice.actions;

// Sélecteurs de base
export const selectDriverProfile = (state) => state.driver.profile;
export const selectDriverStats = (state) => state.driver.stats;
export const selectDriverDetailedStats = (state) => state.driver.detailedStats;
export const selectDriverEarnings = (state) => state.driver.earnings;
export const selectDriverDetailedEarnings = (state) => state.driver.detailedEarnings;
export const selectCurrentDeliveries = (state) => {
  const data = state.driver.currentDeliveries;
  if (Array.isArray(data)) return data;
  return data?.deliveries || [];
};
export const selectAvailableDeliveries = (state) => {
  const data = state.driver.availableDeliveries;
  if (Array.isArray(data)) return data;
  return data?.deliveries || [];
};
export const selectDeliveryHistory = (state) => state.driver.deliveryHistory?.deliveries || [];
// Forme brute { deliveries, pagination } -- pour DriverHistory.js qui lit les
// deux, contrairement a selectDeliveryHistory (tableau plat) utilise par MissionsPage.
export const selectDeliveryHistoryFull = (state) => state.driver.deliveryHistory || { deliveries: [], pagination: null };
export const selectCurrentDelivery = (state) => state.driver.currentDelivery;
export const selectIsAvailable = (state) => state.driver.isAvailable;
export const selectDriverStatus = (state) => state.driver.status;
export const selectCurrentLocation = (state) => state.driver.currentLocation;
export const selectDriverLoading = (state) => state.driver.loading;
export const selectDriverError = (state) => state.driver.error;

// Sélecteurs de loading spécifiques
export const selectStatusLoading = (state) => state.driver.loading.status;
export const selectAvailabilityLoading = (state) => state.driver.loading.availability;
export const selectAvailableDeliveriesLoading = (state) => state.driver.loading.availableDeliveries;
export const selectCurrentDeliveriesLoading = (state) => state.driver.loading.currentDeliveries;
export const selectLocationLoading = (state) => state.driver.loading.location;
export const selectStatsLoading = (state) => state.driver.loading.stats;
export const selectEarningsLoading = (state) => state.driver.loading.earnings;
export const selectClaimDeliveryLoading = (state) => state.driver.loading.claimDelivery;
export const selectUpdateDeliveryStatusLoading = (state) => state.driver.loading.updateDeliveryStatus;

// Sélecteurs calculés
export const selectCanAcceptDeliveries = (state) => {
  const { isAvailable, status, loading, profile } = state.driver;
  
  // Vérifier la disponibilité de base
  if (!isAvailable || (status !== 'online' && status !== 'available')) {
    return false;
  }
  
  // Vérifier les chargements en cours
  if (loading.availableDeliveries || loading.status || loading.claimDelivery) {
    return false;
  }
  
  // Vérifier la capacité maximum si le profil est chargé
  if (profile && profile.current_deliveries_count >= profile.max_concurrent_deliveries) {
    return false;
  }
  
  return true;
};

export const selectDeliveryCapacity = (state) => {
  const profile = state.driver.profile;
  if (!profile) return null;
  
  return {
    current: profile.current_deliveries_count || 0,
    max: profile.max_concurrent_deliveries || 3,
    available: (profile.max_concurrent_deliveries || 3) - (profile.current_deliveries_count || 0)
  };
};

export const selectDriverPerformance = (state) => {
  const profile = state.driver.profile;
  if (!profile) return null;

  // getDriverProfile() (backend) imbrique ces stats sous profile.performance,
  // jamais a la racine -- lire profile.total_deliveries etc. directement
  // retombait toujours sur les valeurs par defaut (100%, 0 livraison).
  const perf = profile.performance || {};
  const total = perf.total_deliveries || 0;
  const successful = perf.successful_deliveries || 0;

  return {
    total_deliveries: total,
    successful_deliveries: successful,
    failed_deliveries: perf.failed_deliveries || 0,
    success_rate: perf.success_rate ?? (total > 0 ? Math.round((successful / total) * 100) : 100),
    driver_rating: parseFloat(perf.driver_rating || 0),
    acceptance_rate: parseFloat(perf.acceptance_rate || 100),
    on_time_rate: parseFloat(perf.on_time_rate || 100)
  };
};

export default driverSlice.reducer;